import React, { useState, useEffect, useCallback } from 'react';
import {
  Pane,
  Text,
  Button,
  Spinner,
  toaster,
  Heading,
  Badge,
  IconButton,
  ChevronDownIcon,
  ChevronRightIcon
} from 'evergreen-ui';
import CommentEditor from './CommentEditor';
import CommentItem from './CommentItem';
import api from '../../services/api';

const CommentSection = ({
  projectId,
  entityType,
  entityId,
  currentUserId,
  compact = false,
  forceShow = false,
  manuallyCollapsed = false,
  onAutoExpand = null,
  onCollapse = null
}) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

  const fetchComments = useCallback(async (loadMore = false) => {
    if (!projectId || !entityId) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        [entityType]: entityId,
        include_replies: 'true',
        limit: '20',
        offset: loadMore ? offset.toString() : '0'
      });

      const response = await api.get(`/projects/${projectId}/comments?${params}`);
      
      if (loadMore) {
        setComments(prev => [...prev, ...response.data.comments]);
      } else {
        setComments(response.data.comments);
      }
      
      const total = response.data.total;
      
      setHasMore(response.data.has_more);
      setOffset(response.data.offset);
      setTotalCount(total || 0);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toaster.danger('Failed to fetch comments');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityId, entityType, offset]);
  
  const loadMoreComments = useCallback(() => {
    fetchComments(true);
  }, [fetchComments]);

  const fetchInitialComments = useCallback(async () => {
    if (!projectId || !entityId) return;

    console.log(`CommentSection ${entityType}:${entityId} - Starting initial fetch`);
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        [entityType]: entityId,
        include_replies: 'true',
        limit: '20',
        offset: '0'
      });

      console.log(`CommentSection ${entityType}:${entityId} - Fetching with params:`, params.toString());
      const response = await api.get(`/projects/${projectId}/comments?${params}`);
      
      console.log(`CommentSection ${entityType}:${entityId} - Initial fetch response:`, response.data);
      
      setComments(response.data.comments);
      setHasMore(response.data.has_more);
      setOffset(response.data.offset);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching initial comments:', error);
      toaster.danger('Failed to fetch comments');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityId, entityType]);

  // Fetch comments on mount (full mode) or when expanded (compact mode)
  useEffect(() => {
    // Reset hasAutoExpanded when entityId changes (new entity)
    setHasAutoExpanded(false);
    // In compact mode, don't fetch until the section is shown
    if (compact && !forceShow) return;
    fetchInitialComments();
  }, [fetchInitialComments, entityId, compact, forceShow]);

  // Sync internal state with forceShow prop
  useEffect(() => {
    setIsExpanded(forceShow);
  }, [forceShow]);
  
  // Auto-expand after comments are fetched if comments exist
  // Only auto-expand once on initial mount when comments are first loaded
  // Don't auto-expand if user manually collapsed it
  useEffect(() => {
    if (totalCount > 0 && compact && !forceShow && !hasAutoExpanded && !manuallyCollapsed && onAutoExpand) {
      // Auto-expand only on initial load when comments exist and not already expanded
      // Don't auto-expand if user manually collapsed it
      // This will trigger onAutoExpand to update parent state
      console.log(`CommentSection ${entityType}:${entityId} - Auto-expanding due to comments existing`);
      onAutoExpand(`${entityType}_${entityId}`);
      setHasAutoExpanded(true);
    }
  }, [totalCount, compact, hasAutoExpanded, manuallyCollapsed, entityType, entityId, onAutoExpand, forceShow]);
  
  // Don't reset hasAutoExpanded when forceShow changes - we want to remember that we already auto-expanded
  // Only reset when component unmounts or entityId changes





  const handlePostComment = async (content, attachments) => {
    setIsPosting(true);
    try {
      const response = await api.post(`/projects/${projectId}/comments`, {
        content,
        [entityType]: entityId
      });

      // Handle attachments if any
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.file) {
            const formData = new FormData();
            formData.append('file', attachment.file);
            if (attachment.description) {
              formData.append('description', attachment.description);
            }
            
            await api.post(
              `/projects/${projectId}/comments/${response.data.id}/attachments`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          }
        }
      }

      // Refresh comments to get the new one with attachments
      await fetchComments();
      toaster.success('Comment posted successfully');
    } catch (error) {
      console.error('Error posting comment:', error);
      toaster.danger('Failed to post comment');
    } finally {
      setIsPosting(false);
    }
  };

  const handleReply = (newReply) => {
    // Refresh comments instead of manually updating to avoid duplication
    fetchComments();
  };

  const handleEdit = (commentId, updatedComment) => {
    const updateComments = (commentList) => {
      return commentList.map(comment => {
        if (comment.id === commentId) {
          return updatedComment;
        }
        if (comment.replies) {
          return {
            ...comment,
            replies: updateComments(comment.replies)
          };
        }
        return comment;
      });
    };

    setComments(updateComments(comments));
  };

  const handleDelete = (commentId) => {
    const removeComment = (commentList) => {
      return commentList.filter(comment => {
        if (comment.id === commentId) {
          return false;
        }
        if (comment.replies) {
          comment.replies = removeComment(comment.replies);
        }
        return true;
      });
    };

    setComments(removeComment(comments));
  };

  const getTotalCommentCount = () => {
    const countComments = (commentList) => {
      return commentList.reduce((total, comment) => {
        return total + 1 + (comment.replies ? countComments(comment.replies) : 0);
      }, 0);
    };
    return countComments(comments);
  };



  // Compact mode with toggle
  if (compact) {
    console.log(`CommentSection ${entityType}:${entityId} - Compact mode render:`, {
      isLoading,
      totalCount,
      isExpanded,
      forceShow,
      comments: comments.length
    });

    // If still loading, show loading state
    if (isLoading) {
      console.log(`CommentSection ${entityType}:${entityId} - Rendering loading state`);
      return (
        <Pane 
          display="flex" 
          alignItems="center" 
          padding={8}
          borderRadius={4}
        >
          <Spinner size={16} marginRight={8} />
          <Text size={400} color="muted">
            Loading comments...
          </Text>
        </Pane>
      );
    }

    // If no comments and not manually expanded, don't render anything
    // The button is handled by the parent component (Results.js)
    if (totalCount === 0 && !isExpanded && !forceShow) {
      console.log(`CommentSection ${entityType}:${entityId} - No comments, not rendering anything`);
      return null;
    }

    // If comments exist or manually expanded, show full section
    // Note: In compact mode, expansion is controlled by parent via forceShow prop
    // The toggle button is in Results.js, not here
    console.log(`CommentSection ${entityType}:${entityId} - Rendering full section, forceShow: ${forceShow}`);
    
    // Only render if forceShow is true (controlled by parent)
    if (!forceShow) {
      return null;
    }
    
    return (
      <Pane>
        <Pane marginTop={8} border="1px solid #E6E8F0" borderRadius={6} backgroundColor="tint1">
            {/* Collapse button */}
            <Pane display="flex" justifyContent="flex-end" padding={8}>
              <Button
                size="small"
                appearance="minimal"
                iconBefore={ChevronDownIcon}
                onClick={() => {
                  // Use onCollapse to explicitly close the comments section
                  // Parent will handle marking as manually collapsed
                  if (onCollapse) {
                    onCollapse(`${entityType}_${entityId}`);
                  }
                }}
              >
                Collapse Comments
              </Button>
            </Pane>
            
            {/* Comments List */}
            {comments.length === 0 ? (
              <Pane 
                textAlign="center" 
                padding={32} 
                backgroundColor="tint1" 
                borderRadius={6}
                border="1px solid #E6E8F0"
              >
                <Text color="muted">
                  No comments yet. Be the first to comment!
                </Text>
              </Pane>
            ) : (
              <Pane marginBottom={24}>
                {comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onUpdate={handleEdit}
                    currentUserId={currentUserId}
                    projectId={projectId}
                    entityType={entityType}
                    entityId={entityId}
                  />
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <Pane display="flex" justifyContent="center" marginTop={16}>
                    <Button
                      appearance="minimal"
                      onClick={loadMoreComments}
                      isLoading={isLoading}
                    >
                      Load More Comments
                    </Button>
                  </Pane>
                )}
              </Pane>
            )}

            {/* Comment Editor - only show when expanded */}
            <CommentEditor
              isLoading={isPosting}
              onSave={handlePostComment}
              onCancel={() => {}}
            />
          </Pane>
      </Pane>
    );
  }

  // Full mode - always show comments and editor
  console.log(`CommentSection ${entityType}:${entityId} - Rendering full mode, totalCount:`, totalCount);
  return (
    <Pane>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={500}>
          Comments ({getTotalCommentCount()})
        </Heading>
      </Pane>

      {/* Comments List */}
      {isLoading && comments.length === 0 ? (
        <Pane display="flex" justifyContent="center" padding={32}>
          <Spinner />
        </Pane>
      ) : comments.length === 0 ? (
        <Pane 
          textAlign="center" 
          padding={32} 
          backgroundColor="tint1" 
          borderRadius={6}
          border="1px solid #E6E8F0"
        >
          <Text color="muted">
            No comments yet. Be the first to comment!
          </Text>
        </Pane>
      ) : (
        <Pane marginBottom={24}>
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onReply={handleReply}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onUpdate={handleEdit}
              currentUserId={currentUserId}
              projectId={projectId}
              entityType={entityType}
              entityId={entityId}
            />
          ))}

          {/* Load More Button */}
          {hasMore && (
            <Pane display="flex" justifyContent="center" marginTop={16}>
              <Button
                appearance="minimal"
                onClick={loadMoreComments}
                isLoading={isLoading}
              >
                Load More Comments
              </Button>
            </Pane>
          )}
        </Pane>
      )}

      {/* Comment Editor - always show in full mode */}
      <Pane>
        <CommentEditor
          isLoading={isPosting}
          onSave={handlePostComment}
          onCancel={() => {}}
        />
      </Pane>
    </Pane>
  );
};

export default CommentSection;
